import { toast } from 'react-toastify';

import useSearchStatusStore from '@/apps/mwd-v2/state/selectors/useSearchStatusStore';
import { SearchParams, SearchRequest, SearchRequests } from '@/components/search/types';
import { useFetchWithAuth } from '@/queries/useFetchWithAuth';
import useWerklijnenSubscription from '@/state/selectors/useWerklijnenSubscription';
import useClientState from '@/state/useClientState';
import { WerklijnInline } from '@/types/werklijnenInline';
import { toastInfoOptions } from '@/utils/toastOptions';

import searchParamsRequests from '../v1/utils/searchCriteria';

/**
 * v2 Implementation for the searchApi.
 */
function useSearchApi() {
  const { isSearching, setIsSearching, setShowNoSearchResults, setShowNothingToShow } = useSearchStatusStore();
  const { vervangWerklijnen, voegMeerdereWerklijnenToe } = useWerklijnenSubscription();
  const hideDetailPanel = useClientState((state) => state.hideDetailPanel);
  const { fetchWithAuth } = useFetchWithAuth();

  /**
   * @param searchRequests
   * @returns
   */
  async function fetchSearchRequests(searchRequests: SearchRequests): Promise<WerklijnInline[]> {
    const responses = await Promise.all(
      searchRequests.map((request) =>
        fetchWithAuth('/werklijnen/zoek', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(request),
        }),
      ),
    );

    // Yield data from the responses.
    const data = (await Promise.all(responses.filter((res) => res.ok).map((res) => res.json())))
      .filter((data) => Object.keys(data).includes('werklijnen'))
      .flatMap((data) => data.werklijnen);

    return data ?? [];
  }

  type SearchInputParamsOptions = {
    add?: boolean;
    toppen?: boolean;
    suppressToast?: boolean;
    redirectNothingToShow?: boolean;
    redirectNoSearchResult?: boolean;
    showErrorToast?: boolean;
    callHideDetailPanel?: boolean;
  };

  const handleSearchStatus = (
    werklijnen: WerklijnInline[],
    {
      add = false,
      toppen = false,
      suppressToast = false,
      redirectNothingToShow = false,
      redirectNoSearchResult = false,
      showErrorToast = false,
      callHideDetailPanel = true,
    }: SearchInputParamsOptions,
  ) => {
    const hasNoResult = werklijnen.length === 0;
    if (redirectNoSearchResult) setShowNoSearchResults(hasNoResult, add);
    if (redirectNothingToShow) {
      setShowNoSearchResults(false);
      setShowNothingToShow(hasNoResult);
    }
    if (add) {
      voegMeerdereWerklijnenToe(werklijnen, toppen, suppressToast);
    } else {
      vervangWerklijnen(werklijnen);
      if (callHideDetailPanel) hideDetailPanel();
    }
    if (showErrorToast && hasNoResult) toast.info('Geen resultaten gevonden', toastInfoOptions);
  };

  async function searchAdvanced(
    advancedSearchParams: SearchRequests | SearchRequest,
    {
      add = false,
      toppen = false,
      suppressToast = false,
      redirectNothingToShow = false,
      redirectNoSearchResult = false,
      showErrorToast = false,
      callHideDetailPanel = true,
    }: SearchInputParamsOptions,
  ): Promise<WerklijnInline[]> {
    const werklijnen = await fetchSearchRequests(
      Array.isArray(advancedSearchParams) ? advancedSearchParams : [advancedSearchParams],
    );
    handleSearchStatus(werklijnen, {
      add,
      toppen,
      suppressToast,
      redirectNothingToShow,
      redirectNoSearchResult,
      showErrorToast,
      callHideDetailPanel,
    });
    return werklijnen;
  }

  /**
   * Search the user input from the regular extended search or from the advanced search bar.
   */
  async function searchInputParams(
    searchParams: SearchParams,
    { add = false, toppen = false }: SearchInputParamsOptions,
  ): Promise<WerklijnInline[]> {
    setIsSearching(true, searchParams, add);
    try {
      const searchRequests = searchParamsRequests(searchParams);
      if (!searchRequests.length) return [];

      const werklijnen = await fetchSearchRequests(searchRequests);
      handleSearchStatus(werklijnen, {
        add,
        toppen,
        redirectNothingToShow: true,
        showErrorToast: true,
      });
      return werklijnen;
    } catch (__err) {
      console.error('searchInputParams resulted in an error:', { error: __err, searchParams, add, toppen });
      return [];
    } finally {
      setIsSearching(false);
    }
  }

  return {
    fetchSearchRequests,
    searchAdvanced,
    searchInputParams,
    handleSearchStatus,
    isFetching: isSearching,
  };
}
export default useSearchApi;
